/**
 * Copyright 2024-present, TIENDUC.
 * All rights reserved.
 * @author ductt24@ghtk.vn on 28/10/2024.
 * @module WMS REDUCER
 */

import { createSlice } from "@reduxjs/toolkit";
import wmsThunks from "./thunks";
import { filter, get, round, size, sum } from "lodash";

const slice = createSlice({
  name: "WMS",
  initialState: {
    fetchingSortingDevices: false,
    fetchingProvinces: false,
    fetchStations: false,
    fetchUsers: false,
    stations: [],
    sortingDevices: [],
    provinces: [],
    users: [],
    wheel: {
      sortingDevices: [],
      sortingRate: {
        fetching: false,
        data: {},
        chartDataZoneA: [],
        chartDataZoneB: [],
      },
      statisticSorting: {
        fetching: false,
        data: {},
        chartData: [],
      },

      statisticInduction: {
        fetching: false,
        data: {},
        chartData: [],
      },
      statisticSortingTotal: {
        fetching: false,
        data: {
          today: 0,
          yesterday: 0,
          lastweek: 0,

          todayMax: 0,
          yesterdayMax: 0,
          lastweekMax: 0,

          percent: 0,
          status: false,
          percentLastWeek: 0,
          statusLastweek: false,
          percentMax: 0,
          statusMax: false,
          percentLastWeekMax: 0,
          statusLastweekMax: false,
          subToday: "",
          subYesterDay: "",
          subLastweek: "",
        },
      },
      statisticInductionTotal: {
        fetching: false,
        data: {
          today: 0,
          yesterday: 0,
          lastweek: 0,

          todayMax: 0,
          yesterdayMax: 0,
          lastweekMax: 0,

          percent: 0,
          status: false,
          percentLastWeek: 0,
          statusLastweek: false,
          subToday: "",
          subYesterDay: "",
          subLastweek: "",
        },
      },
    },
    crossbelt: {
      sortingDevices: [],
      inboundRate: {
        fetching: false,
        data: {},
        chartDataZoneA: [],
        chartDataZoneB: [],
      },
      scannerRate: {
        fetching: false,
        data: {},
        chartDataZoneA: [],
        chartDataZoneB: [],
      },
      statisticSorting: {
        fetching: false,
        data: {},
        chartData: [],
      },

      statisticInduction: {
        fetching: false,
        data: {},
        chartData: [],
      },
      statisticSortingTotal: {
        fetching: false,
        data: {
          today: 0,
          yesterday: 0,
          lastweek: 0,

          todayMax: 0,
          yesterdayMax: 0,
          lastweekMax: 0,

          percent: 0,
          status: false,
          percentLastWeek: 0,
          statusLastweek: false,
          percentMax: 0,
          statusMax: false,
          percentLastWeekMax: 0,
          statusLastweekMax: false,
          subToday: "",
          subYesterDay: "",
          subLastweek: "",
        },
      },
      statisticInductionTotal: {
        fetching: false,
        data: {
          today: 0,
          yesterday: 0,
          lastweek: 0,

          todayMax: 0,
          yesterdayMax: 0,
          lastweekMax: 0,

          percent: 0,
          status: false,
          percentLastWeek: 0,
          statusLastweek: false,
          subToday: "",
          subYesterDay: "",
          subLastweek: "",
        },
      },
    },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(wmsThunks.fetchSortingDevices.pending, (state, action) => {
      state.fetchingSortingDevices = true;
    });
    builder.addCase(wmsThunks.fetchSortingDevices.rejected, (state, action) => {
      state.fetchingSortingDevices = false;
    });
    builder.addCase(
      wmsThunks.fetchSortingDevices.fulfilled,
      (state, action) => {
        let results = get(action.payload, "results", []);
        state.sortingDevices = results;
        state.fetchingSortingDevices = false;
        state.crossbelt.sortingDevices = filter(
          results,
          (x) => x.device_type === "crossbelt"
        );
        state.wheel.sortingDevices = filter(
          results,
          (x) => x.device_type === "wheel"
        );
      }
    );

    builder.addCase(wmsThunks.fetchProvinces.pending, (state, action) => {
      state.fetchingProvinces = true;
    });
    builder.addCase(wmsThunks.fetchProvinces.rejected, (state, action) => {
      state.fetchingProvinces = false;
    });
    builder.addCase(wmsThunks.fetchProvinces.fulfilled, (state, action) => {
      let results = get(action.payload, "results", []);
      state.provinces = results;
      state.fetchingProvinces = false;
    });
    
    builder.addCase(wmsThunks.fetchUsers.pending, (state, action) => {
      state.fetchUsers = true;
    });
    builder.addCase(wmsThunks.fetchUsers.rejected, (state, action) => {
      state.fetchUsers = false;
    });
    builder.addCase(wmsThunks.fetchUsers.fulfilled, (state, action) => {
      let results = get(action.payload, "results", []);
      state.users = results;
      state.fetchUsers = false;
    });


    builder.addCase(wmsThunks.fetchStations.pending, (state, action) => {
      state.fetchStations = true;
    });
    builder.addCase(wmsThunks.fetchStations.rejected, (state, action) => {
      state.fetchStations = false;
    });
    builder.addCase(wmsThunks.fetchStations.fulfilled, (state, action) => {
      state.fetchStations = false;
      let results = get(action.payload, "results", []);
      state.stations = results;
    });
    builder.addCase(wmsThunks.fetchCrossbeltInboundRate.pending, (state) => {
      state.crossbelt.inboundRate.fetching = true;
    });
    builder.addCase(wmsThunks.fetchCrossbeltInboundRate.rejected, (state) => {
      state.crossbelt.inboundRate.fetching = false;
    });
    builder.addCase(
      wmsThunks.fetchCrossbeltInboundRate.fulfilled,
      (state, action) => {
        let inputData = get(action, "payload", {});
        state.crossbelt.inboundRate.fetching = false;
        if (size(inputData) > 0) {
          // Lấy dữ liệu cho Pie Chart A (index 0)
          const chartDataA = getChartRateData(inputData, 0);

          // Lấy dữ liệu cho Pie Chart B (index 1)
          const chartDataB = getChartRateData(inputData, 1);
          state.crossbelt.inboundRate.data = inputData;
          state.crossbelt.inboundRate.chartDataZoneA = chartDataA;
          state.crossbelt.inboundRate.chartDataZoneB = chartDataB;
        }
      }
    );

    builder.addCase(wmsThunks.fetchCrossbeltScannerRate.pending, (state) => {
      state.crossbelt.scannerRate.fetching = true;
    });
    builder.addCase(wmsThunks.fetchCrossbeltScannerRate.rejected, (state) => {
      state.crossbelt.scannerRate.fetching = false;
    });
    builder.addCase(
      wmsThunks.fetchCrossbeltScannerRate.fulfilled,
      (state, action) => {
        state.crossbelt.scannerRate.fetching = false;
        let inputData = get(action, "payload", {});
        if (size(inputData) > 0) {
          // Lấy dữ liệu cho Pie Chart A (index 0)
          const chartDataA = getChartRateData(inputData, 0);

          // Lấy dữ liệu cho Pie Chart B (index 1)
          const chartDataB = getChartRateData(inputData, 1);
          state.crossbelt.scannerRate.data = inputData;
          state.crossbelt.scannerRate.chartDataZoneA = chartDataA;
          state.crossbelt.scannerRate.chartDataZoneB = chartDataB;
        }
      }
    );

    // Crossbelt Statistic Induction
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticInduction.pending,
      (state) => {
        state.crossbelt.statisticInduction.fetching = true;
      }
    );
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticInduction.rejected,
      (state) => {
        state.crossbelt.statisticInduction.fetching = false;
      }
    );
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticInduction.fulfilled,
      (state, action) => {
        state.crossbelt.statisticInduction.fetching = false;
        let inputData = get(action, "payload", {});
        if (size(inputData) > 0) {
          let series = inputData.datasets.map((dataset) => ({
            name: dataset.label,
            data: dataset.data,
            color: dataset.borderColor,
          }));
          let labels = inputData.labels;
          state.crossbelt.statisticInduction.data = inputData;
          state.crossbelt.statisticInduction.chartData = {
            series:
              size(series) > 0
                ? series
                : [{ showInLegend: false, data: labels.map(() => null) }],
            labels,
          };
        }
      }
    );

    // Crossbelt Statistic Sorting
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticSorting.pending,
      (state) => {
        state.crossbelt.statisticSorting.fetching = false;
      }
    );
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticSorting.rejected,
      (state) => {
        state.crossbelt.statisticSorting.fetching = false;
      }
    );
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticSorting.fulfilled,
      (state, action) => {
        state.crossbelt.statisticSorting.fetching = false;
        let inputData = get(action, "payload", {});
        if (size(inputData) > 0) {
          let series = inputData.datasets.map((dataset) => ({
            name: "",
            data: dataset.data,
            color: dataset.borderColor,
            dataLabels: {
              enabled: true, // Hiển thị giá trị trên mỗi cột
              style: {
                fontSize: "12px",
                color: "#FFFFFF",
              },
            },
          }));
          let labels = inputData.labels;
          state.crossbelt.statisticSorting.data = inputData;
          state.crossbelt.statisticSorting.chartData = {
            series:
              size(series) > 0
                ? series
                : [{ showInLegend: false, data: labels.map(() => null) }],
            labels,
          };
        }
      }
    );

    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticSortingTotal.pending,
      (state) => {
        state.crossbelt.statisticSortingTotal.fetching = true;
      }
    );
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticSortingTotal.rejected,
      (state) => {
        state.crossbelt.statisticSortingTotal.fetching = false;
      }
    );
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticSortingTotal.fulfilled,
      (state, action) => {
        state.crossbelt.statisticSortingTotal.fetching = false;
        let inputData = get(action, "payload", {});
        let totalToday = inputData.today;
        let totalYesterDay = inputData.yesterday;
        let totalLastWeek = inputData.lastWeek;

        let todayDatasets = get(totalToday, "datasets[0].data", []);
        let yesterdayDatasets = get(totalYesterDay, "datasets[0].data", []);
        let lastweekDatasets = get(totalLastWeek, "datasets[0].data", []);

        let data = state.crossbelt.statisticSortingTotal;
        data.today = sum(todayDatasets);
        data.todayMax = totalToday.max || 0;
        data.subToday = totalToday.max_at || "00:00";

        data.yesterday = sum(yesterdayDatasets);
        data.yesterdayMax = totalYesterDay.max || 0;
        data.subYesterDay = totalYesterDay.max_at || "00:00";

        data.lastweek = sum(lastweekDatasets);
        data.lastweekMax = totalLastWeek.max || 0;
        data.subLastweek = totalLastWeek.max_at || "00:00";

        const { percent: percentMax, status: statusMax } = getPercentAndStatus(
          data.todayMax,
          data.yesterdayMax
        );
        const { percent: percentLastWeekMax, status: statusLastweekMax } =
          getPercentAndStatus(data.todayMax, data.lastweekMax);
        data.percentMax = round(percentMax, 2);
        data.statusMax = statusMax;
        data.percentLastWeekMax = round(percentLastWeekMax, 2);
        data.statusLastweekMax = statusLastweekMax;

        const { percent, status } = getPercentAndStatus(
          data.today,
          data.yesterday
        );
        const { percent: percentLastWeek, status: statusLastweek } =
          getPercentAndStatus(data.today, data.lastweek);
        data.percent = round(percent, 2);
        data.status = status;
        data.percentLastWeek = round(percentLastWeek, 2);
        data.statusLastweek = statusLastweek;
      }
    );

    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticInductionTotal.pending,
      (state) => {
        state.crossbelt.statisticInductionTotal.fetching = true;
      }
    );
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticInductionTotal.rejected,
      (state) => {
        state.crossbelt.statisticInductionTotal.fetching = false;
      }
    );
    builder.addCase(
      wmsThunks.fetchCrossbeltStatisticInductionTotal.fulfilled,
      (state, action) => {
        state.crossbelt.statisticInductionTotal.fetching = false;
        let inputData = get(action, "payload", {});
        let data = state.crossbelt.statisticInductionTotal;
        console.log("fetchCrossbeltStatisticInductionTotal", inputData);
        let totalToday = inputData.today;
        let totalYesterDay = inputData.yesterday;
        let totalLastWeek = inputData.lastWeek;

        data.today = totalToday.max || 0;
        data.todayMax = totalToday.max || 0;
        data.subToday = totalToday
          ? `IDT ${totalToday.max_induction || 0} | ${
              totalToday.max_at || "00:00"
            }`
          : "IDT 0 | 00:00";

        data.yesterday = totalYesterDay.max || 0;
        data.yesterdayMax = totalYesterDay.max || 0;
        data.subYesterDay = totalYesterDay
          ? `IDT ${totalYesterDay.max_induction || 0} | ${
              totalYesterDay.max_at || "00:00"
            }`
          : "IDT 0 | 00:00";

        data.lastweek = totalLastWeek.max || 0;
        data.lastweekMax = totalLastWeek.max || 0;
        data.subLastweek = totalLastWeek
          ? `IDT ${totalLastWeek.max_induction || 0} | ${
              totalLastWeek.max_at || "00:00"
            }`
          : "IDT 0 | 00:00";

        const { percent, status } = getPercentAndStatus(
          data.today,
          data.yesterday
        );
        const { percent: percentLastWeek, status: statusLastweek } =
          getPercentAndStatus(data.today, data.lastweek);
        data.percent = round(percent, 2);
        data.status = status;
        data.percentLastWeek = round(percentLastWeek, 2);
        data.statusLastweek = statusLastweek;
      }
    );

    builder.addCase(wmsThunks.fetchWheelSortingRate.pending, (state) => {
      state.wheel.sortingRate.fetching = true;
    });
    builder.addCase(wmsThunks.fetchWheelSortingRate.rejected, (state) => {
      state.wheel.sortingRate.fetching = false;
    });
    builder.addCase(
      wmsThunks.fetchWheelSortingRate.fulfilled,
      (state, action) => {
        state.wheel.sortingRate.fetching = false;
        let inputData = get(action, "payload", {});
        if (size(inputData) > 0) {
          // Lấy dữ liệu cho Pie Chart A (index 0)
          const chartDataA = getChartRateData(inputData, 0);

          // Lấy dữ liệu cho Pie Chart B (index 1)
          const chartDataB = getChartRateData(inputData, 1);
          state.wheel.sortingRate.data = inputData;
          state.wheel.sortingRate.chartDataZoneA = chartDataA;
          state.wheel.sortingRate.chartDataZoneB = chartDataB;
        }
      }
    );

    // Wheel Statistic Induction
    builder.addCase(wmsThunks.fetchWheelStatisticInduction.pending, (state) => {
      state.wheel.statisticInduction.fetching = true;
    });
    builder.addCase(
      wmsThunks.fetchWheelStatisticInduction.rejected,
      (state) => {
        state.wheel.statisticInduction.fetching = false;
      }
    );
    builder.addCase(
      wmsThunks.fetchWheelStatisticInduction.fulfilled,
      (state, action) => {
        state.wheel.statisticInduction.fetching = false;
        let inputData = get(action, "payload", {});
        if (size(inputData) > 0) {
          let series = inputData.datasets.map((dataset) => ({
            name: dataset.label,
            data: dataset.data,
            color: dataset.borderColor,
          }));
          let labels = inputData.labels;
          state.wheel.statisticInduction.data = inputData;
          state.wheel.statisticInduction.chartData = {
            series:
              size(series) > 0
                ? series
                : [{ showInLegend: false, data: labels.map(() => null) }],
            labels,
          };
        }
      }
    );

    // Wheel Statistic Sorting
    builder.addCase(wmsThunks.fetchWheelStatisticSorting.pending, (state) => {
      state.wheel.statisticSorting.fetching = false;
    });
    builder.addCase(wmsThunks.fetchWheelStatisticSorting.rejected, (state) => {
      state.wheel.statisticSorting.fetching = false;
    });
    builder.addCase(
      wmsThunks.fetchWheelStatisticSorting.fulfilled,
      (state, action) => {
        state.wheel.statisticSorting.fetching = false;
        let inputData = get(action, "payload", {});
        if (size(inputData) > 0) {
          let series = inputData.datasets.map((dataset) => ({
            name: "",
            data: dataset.data,
            color: dataset.borderColor,
            dataLabels: {
              enabled: true, // Hiển thị giá trị trên mỗi cột
              style: {
                fontSize: "12px",
                color: "#FFFFFF",
              },
            },
          }));
          let labels = inputData.labels;
          state.wheel.statisticSorting.data = inputData;
          state.wheel.statisticSorting.chartData = {
            series:
              size(series) > 0
                ? series
                : [{ showInLegend: false, data: labels.map(() => null) }],
            labels,
          };
        }
      }
    );

    builder.addCase(
      wmsThunks.fetchWheelStatisticSortingTotal.pending,
      (state) => {
        state.wheel.statisticSortingTotal.fetching = true;
      }
    );
    builder.addCase(
      wmsThunks.fetchWheelStatisticSortingTotal.rejected,
      (state) => {
        state.wheel.statisticSortingTotal.fetching = false;
      }
    );
    builder.addCase(
      wmsThunks.fetchWheelStatisticSortingTotal.fulfilled,
      (state, action) => {
        state.wheel.statisticSortingTotal.fetching = false;
        let inputData = get(action, "payload", {});
        let totalToday = inputData.today;
        let totalYesterDay = inputData.yesterday;
        let totalLastWeek = inputData.lastWeek;

        let todayDatasets = get(totalToday, "datasets[0].data", []);
        let yesterdayDatasets = get(totalYesterDay, "datasets[0].data", []);
        let lastweekDatasets = get(totalLastWeek, "datasets[0].data", []);

        let data = state.wheel.statisticSortingTotal;
        data.today = sum(todayDatasets);
        data.todayMax = totalToday.max || 0;
        data.subToday = totalToday.max_at || "00:00";

        data.yesterday = sum(yesterdayDatasets);
        data.yesterdayMax = totalYesterDay.max || 0;
        data.subYesterDay = totalYesterDay.max_at || "00:00";

        data.lastweek = sum(lastweekDatasets);
        data.lastweekMax = totalLastWeek.max || 0;
        data.subLastweek = totalLastWeek.max_at || "00:00";

        const { percent: percentMax, status: statusMax } = getPercentAndStatus(
          data.todayMax,
          data.yesterdayMax
        );
        const { percent: percentLastWeekMax, status: statusLastweekMax } =
          getPercentAndStatus(data.todayMax, data.lastweekMax);
        data.percentMax = round(percentMax, 2);
        data.statusMax = statusMax;
        data.percentLastWeekMax = round(percentLastWeekMax, 2);
        data.statusLastweekMax = statusLastweekMax;

        const { percent, status } = getPercentAndStatus(
          data.today,
          data.yesterday
        );
        const { percent: percentLastWeek, status: statusLastweek } =
          getPercentAndStatus(data.today, data.lastweek);
        data.percent = round(percent, 2);
        data.status = status;
        data.percentLastWeek = round(percentLastWeek, 2);
        data.statusLastweek = statusLastweek;
      }
    );

    builder.addCase(
      wmsThunks.fetchWheelStatisticInductionTotal.pending,
      (state) => {
        state.wheel.statisticInductionTotal.fetching = true;
      }
    );
    builder.addCase(
      wmsThunks.fetchWheelStatisticInductionTotal.rejected,
      (state) => {
        state.wheel.statisticInductionTotal.fetching = false;
      }
    );
    builder.addCase(
      wmsThunks.fetchWheelStatisticInductionTotal.fulfilled,
      (state, action) => {
        state.wheel.statisticInductionTotal.fetching = false;
        let inputData = get(action, "payload", {});
        let data = state.wheel.statisticInductionTotal;
        console.log("fetchWheelStatisticInductionTotal", inputData);
        let totalToday = inputData.today;
        let totalYesterDay = inputData.yesterday;
        let totalLastWeek = inputData.lastWeek;

        data.today = totalToday.max || 0;
        data.todayMax = totalToday.max || 0;
        data.subToday = totalToday
          ? `IDT ${totalToday.max_induction || 0} | ${
              totalToday.max_at || "00:00"
            }`
          : "IDT 0 | 00:00";

        data.yesterday = totalYesterDay.max || 0;
        data.yesterdayMax = totalYesterDay.max || 0;
        data.subYesterDay = totalYesterDay
          ? `IDT ${totalYesterDay.max_induction || 0} | ${
              totalYesterDay.max_at || "00:00"
            }`
          : "IDT 0 | 00:00";

        data.lastweek = totalLastWeek.max || 0;
        data.lastweekMax = totalLastWeek.max || 0;
        data.subLastweek = totalLastWeek
          ? `IDT ${totalLastWeek.max_induction || 0} | ${
              totalLastWeek.max_at || "00:00"
            }`
          : "IDT 0 | 00:00";

        const { percent, status } = getPercentAndStatus(
          data.today,
          data.yesterday
        );
        const { percent: percentLastWeek, status: statusLastweek } =
          getPercentAndStatus(data.today, data.lastweek);
        data.percent = round(percent, 2);
        data.status = status;
        data.percentLastWeek = round(percentLastWeek, 2);
        data.statusLastweek = statusLastweek;
      }
    );
  },
});

const getPercentAndStatus = (arg1, arg2) => {
  let status = false;
  let percent = 0;
  if (arg1 !== 0 && arg2 !== 0) {
    status = arg1 > arg2;
    percent = status ? (arg1 / arg2) * 100 - 100 : 100 - (arg1 / arg2) * 100;
  }

  return { percent, status };
};

function getChartRateData(inputData, chartIndex) {
  // Kiểm tra nếu chartIndex hợp lệ
  if (chartIndex < 0 || chartIndex >= inputData.datasets.length) {
    return [];
  }
  // Tính tổng của tất cả các giá trị trong dataset
  const total = inputData.datasets[chartIndex].data.reduce(
    (sum, value) => sum + value,
    0
  );
  // Tạo chartData cho biểu đồ được chỉ định
  return inputData.labels.map((label, index) => {
    const value = inputData.datasets[chartIndex].data[index];
    const percentage = round(total > 0 ? (value / total) * 100 : 0, 1); // Kiểm tra tổng lớn hơn 0 để tránh chia cho 0
    return {
      name: label,
      y: value,
      color: inputData.datasets[chartIndex].borderColor[index],
    };
  });
}

export const wmsActions = slice.actions;
export const wmsReducer = slice.reducer;
